import _get from 'lodash/get'
import type { ReactElement } from 'react'
import React, { useCallback } from 'react'
import type { ListRenderItem } from 'react-native'
import { FlatList, Platform, Text, View, useWindowDimensions } from 'react-native'
import { tv } from 'tailwind-variants'
import { P, match } from 'ts-pattern'

import type Api from '@api-service/api'
import { Language, SupportedMarketKey } from '@constants/format'
import { AccordionList } from '@flows-ui/components/AccordionList'
import { AddressAutocomplete } from '@flows-ui/components/AddressAutocomplete'
import { Animation } from '@flows-ui/components/Animation'
import { Checkbox } from '@flows-ui/components/Checkbox'
import { Choice } from '@flows-ui/components/Choice'
import { CodeInput } from '@flows-ui/components/CodeInput'
import { Date } from '@flows-ui/components/Date'
import { DropDown } from '@flows-ui/components/DropDown'
import { EditableList } from '@flows-ui/components/EditableList'
import { EditableSet } from '@flows-ui/components/EditableSet'
import { Image } from '@flows-ui/components/Image'
import { InfoBox } from '@flows-ui/components/InfoBox'
import { Input } from '@flows-ui/components/Input'
import { ItemActionList } from '@flows-ui/components/ItemActionList'
import { ListedItems } from '@flows-ui/components/ListedItems'
import { MultiSelect } from '@flows-ui/components/MultiSelect'
import { OtpLogin } from '@flows-ui/components/OtpLogin'
import { PhoneNumber } from '@flows-ui/components/PhoneNumber'
import { RadioCardSet } from '@flows-ui/components/RadioCardSet'
import { SearchableList } from '@flows-ui/components/SearchableList'
import { SelectionButtonSet } from '@flows-ui/components/SelectionButtonSet'
import { Stepper } from '@flows-ui/components/Stepper'
import { Tag } from '@flows-ui/components/Tag'
import { Text as TextComponent } from '@flows-ui/components/Text'
import { Title } from '@flows-ui/components/Title'
import type { StepContentRendererProps } from '@flows-ui/types'
import type { AnyStepContentElement } from '@flows/core/types/stepContentTypes'
import { evaluateCondition, evaluateDisabledCondition } from '@flows/renderer/utils/conditions'
import InfoButton from '@ui-kit/components/InfoButton'
import Spinner from '@ui-kit/components/Spinner'
import Columns from '@ui-kit/components/layoutComponents/Columns'
import Spacer from '@ui-kit/components/layoutComponents/Spacer/index'
import { SCREEN_SIZE, SPACE } from '@ui-kit/constants'
import '@ui-kit/theme/unistyles'
import type { Space } from '@ui-kit/types'

import { Alert } from './components/Alert'
import { Calendly } from './components/Calendly'
import { HarmonyTitle } from './components/HarmonyTitle'
import { Icon } from './components/Icon'
import { List } from './components/List'
import { ProgressiveQuestions } from './components/ProgressiveQuestions'
import { RichContent } from './components/RichContent'
import { ApiServiceProvider } from './services/ApiServiceContext'
import { FlowsServicesResponsesProvider } from './services/FlowServicesResponsesContext'

// Re-export quote components and context for consumers
export { QuoteDataProvider, useQuoteData } from './services/QuoteDataContext'
export type {
  QuoteDataContextValue,
  PriceData,
  PriceEntry,
  PriceProductConfiguration,
  PermanentDiscountData,
  ReferralDiscountData,
  CouponData,
  ProductGenerationData,
  PremiumInterval,
  StartDateConfiguration,
} from './services/QuoteDataContext.types'
export { StepQuote } from './components/quote/StepQuote'
export type { StepQuoteProps } from './components/quote/StepQuote'
export { UnderInsuredWarning } from './components/quote/UnderInsuredWarning'
export { DentalQuote } from './components/quote/dental/DentalQuote'
export type { DentalQuoteProps } from './components/quote/dental/DentalQuote'
export { CoverageQuote } from './components/quote/CoverageQuote'
export type { CoverageQuoteProps } from './components/quote/CoverageQuote'
export { ContentsInstantQuoteQuestions } from './components/ContentsInstantQuoteQuestions'
export { LiabilityInstantQuoteQuestions } from './components/LiabilityInstantQuoteQuestions'
export { LiabilityFamilyCoverageInfoAccordions } from './components/LiabilityFamilyCoverageInfoAccordions'
export { DroneOptIn } from './components/DroneOptIn'
export type { DroneOptInProps } from './components/DroneOptIn'
export { SuccessStep } from './components/SuccessStep'
export { getSinglePriceWithInterval } from './utils/getSinglePriceWithInterval'

const webStyles = tv({
  base: 'clear-step-content-z-index flex-1 py-2.5',
  variants: {
    hasMinHeight: {
      true: 'min-h-[calc(100vh-260px)]',
    },
    hasVerticalPadding: {
      false: 'py-0',
    },
  },
})

// Element types that render a focusable HTML form control (<input>/<select>/<textarea>).
// On iOS Safari these get scaled via the .safari-scale CSS rule (see ui-kit reset.css).
const FOCUSABLE_ELEMENT_TYPES = new Set<AnyStepContentElement['type']>([
  'input',
  'date',
  'dropDown',
  'phoneNumber',
  'addressAutocomplete',
  'multiSelect',
  'otpInput',
  'codeInput',
])

const NO_SEPARATORS = {
  highlight: () => undefined,
  unhighlight: () => undefined,
  updateProps: () => undefined,
}

const getElementKey = (item: AnyStepContentElement, index: number) => {
  return 'name' in item && item.name ? `${item.type}-${item.name}` : `${item.type}-${index}`
}

export type RendererLocale = `${Language}-${SupportedMarketKey}`

const getInfoButtonElements = (infoButton: {
  modalConfiguration?: unknown
  tooltipConfiguration?: unknown
}) =>
  (infoButton.modalConfiguration ?? infoButton.tooltipConfiguration ?? []) as Parameters<
    typeof StepContentRenderer
  >[0]['elements']

export const StepContentRenderer = <
  TStepContentRendererProps extends StepContentRendererProps<AnyStepContentElement>,
>(
  props: TStepContentRendererProps & {
    space?: Space
    hasMinHeight?: boolean
    hasVerticalPadding?: boolean
    apiService?: Api
    apiBases?: Readonly<Record<string, string>>
    progressive?: boolean
  }
) => {
  const isDesktop = useWindowDimensions().width >= SCREEN_SIZE.M
  const renderItem: ListRenderItem<AnyStepContentElement> = useCallback(
    (data) => {
      const visible = match(data.item)
        .with(
          {
            condition: P.not(P.nullish),
          },
          (item) => {
            return evaluateCondition(item.condition, props.flowValues)
          }
        )
        .otherwise(() => true)
      if (!visible) {
        return null
      }

      const conditionallyDisabled = match(data.item)
        .with({ disabledConditionConfig: P.not(P.nullish) }, (item) =>
          evaluateDisabledCondition(
            {
              [item.disabledConditionConfig.condition.valuePath]: _get(
                props.flowValues,
                item.disabledConditionConfig.condition.valuePath
              ) as string | number | boolean | undefined,
            },
            item.disabledConditionConfig.condition
          )
        )
        .otherwise(() => false)
      const resolvedElement = conditionallyDisabled
        ? ({ ...data.item, disabled: true } as AnyStepContentElement)
        : data.item

      const rendered = match(resolvedElement)
        .returnType<ReactElement>()
        .with({ type: 'accordionList' }, (item) => {
          return (
            <AccordionList
              {...item}
              flowValues={props.flowValues}
              data={item.data.map((element) => ({
                ...element,
                content: element.content ? (
                  <StepContentRenderer {...props} hasMinHeight={false} elements={element.content} />
                ) : undefined,
                headingRightContent: element.headingRightContent ? (
                  <StepContentRenderer {...props} hasMinHeight={false} elements={element.content} />
                ) : undefined,
              }))}
            />
          )
        })
        .with({ type: 'alert' }, (item) => {
          return <Alert {...item} flowValues={props.flowValues} />
        })
        .with({ type: 'checkbox' }, (item) => {
          return <Checkbox {...item} flowValues={props.flowValues} />
        })
        .with({ type: 'choice' }, (item) => {
          return <Choice {...item} flowValues={props.flowValues} />
        })
        .with({ type: 'date' }, (item) => {
          return <Date {...item} locale={props.locale} flowValues={props.flowValues} />
        })
        .with({ type: 'dropDown' }, (item) => {
          return <DropDown {...item} flowValues={props.flowValues} />
        })
        .with({ type: 'editableList' }, (item) => {
          return (
            <EditableList
              {...item}
              stepContentRendererProps={props}
              flowValues={props.flowValues}
              StepContentRenderer={StepContentRenderer}
              flowName={props.flowName}
            />
          )
        })
        .with({ type: 'editableSet' }, (item) => {
          return (
            <EditableSet
              {...item}
              stepContentRendererProps={{
                ...props,
              }}
              StepContentRenderer={StepContentRenderer}
              flowValues={props.flowValues}
            />
          )
        })
        .with({ type: 'harmonyTitle', infoButton: P.not(P.nullish) }, (item) => {
          const modalContent = (
            <StepContentRenderer
              {...props}
              hasMinHeight={false}
              elements={getInfoButtonElements(item.infoButton)}
            />
          )
          return (
            <HarmonyTitle {...item} modalContent={modalContent} flowValues={props.flowValues} />
          )
        })
        .with({ type: 'harmonyTitle' }, (item) => {
          return <HarmonyTitle {...item} flowValues={props.flowValues} />
        })
        .with({ type: 'icon' }, (item) => {
          return <Icon {...item} />
        })
        .with({ type: 'image' }, (item) => {
          return <Image {...item} />
        })
        .with({ type: 'infoBox', text: P.not(P.nullish) }, (item) => {
          return <InfoBox {...item} flowValues={props.flowValues} />
        })
        .with({ type: 'infoBox', contentConfiguration: P.not(P.nullish) }, (item) => {
          return (
            <InfoBox {...item} flowValues={props.flowValues}>
              <StepContentRenderer
                {...props}
                space="xxsmall"
                hasMinHeight={false}
                hasVerticalPadding={false}
                elements={item.contentConfiguration}
              />
            </InfoBox>
          )
        })
        .with({ type: 'input' }, (item) => {
          return <Input {...item} flowValues={props.flowValues} track={props.track} />
        })
        .with({ type: 'codeInput' }, (item) => {
          return <CodeInput {...item} flowValues={props.flowValues} track={props.track} />
        })
        .with({ type: 'list' }, (item) => {
          return (
            <List
              {...item}
              flowValues={props.flowValues}
              data={item.data.map((element) => ({
                ...element,
                infoButton: element.infoButton ? (
                  <InfoButton
                    type="info"
                    modalContent={
                      <StepContentRenderer
                        {...props}
                        elements={getInfoButtonElements(element.infoButton)}
                      />
                    }
                  />
                ) : undefined,
              }))}
              summaryData={item.summaryData?.map((element) => ({
                ...element,
                infoButton: element.infoButton ? (
                  <InfoButton
                    type="info"
                    modalContent={
                      <StepContentRenderer
                        {...props}
                        elements={getInfoButtonElements(element.infoButton)}
                      />
                    }
                  />
                ) : undefined,
              }))}
            />
          )
        })
        .with({ type: 'phoneNumber' }, (item) => {
          return (
            <PhoneNumber
              {...item}
              locale={props.locale}
              flowValues={props.flowValues}
              track={props.track}
            />
          )
        })
        .with({ type: 'radioCardSet' }, (item) => {
          return <RadioCardSet {...item} flowValues={props.flowValues} />
        })
        .with({ type: 'searchableList' }, (item) => {
          return <SearchableList {...item} locale={props.locale} flowValues={props.flowValues} />
        })
        .with({ type: 'selectionButtonSet' }, (item) => {
          return <SelectionButtonSet {...item} flowValues={props.flowValues} />
        })
        .with({ type: 'spacer' }, (item) => <Spacer size={item.size} />)
        .with({ type: 'spinner' }, () => <Spinner fullScreen />)
        .with({ type: 'subtitle', infoButton: P.not(P.nullish) }, (item) => {
          const modalContent = (
            <StepContentRenderer
              {...props}
              hasMinHeight={false}
              elements={getInfoButtonElements(item.infoButton)}
            />
          )
          return (
            <Title
              {...item}
              isSubtitle
              modalContent={modalContent}
              flowValues={props.flowValues}
              track={props.track}
            />
          )
        })
        .with({ type: 'subtitle' }, (item) => {
          return <Title {...item} isSubtitle flowValues={props.flowValues} />
        })
        .with({ type: 'tag' }, (item) => {
          return <Tag {...item} flowValues={props.flowValues} />
        })
        .with({ type: 'avatar' }, () => <></>)
        .with({ type: 'text' }, (item) => {
          return <TextComponent {...item} flowValues={props.flowValues} />
        })
        .with({ type: 'title', infoButton: P.not(P.nullish) }, (item) => {
          const modalContent = (
            <StepContentRenderer
              {...props}
              hasMinHeight={false}
              elements={getInfoButtonElements(item.infoButton)}
            />
          )
          return (
            <Title
              {...item}
              modalContent={modalContent}
              flowValues={props.flowValues}
              track={props.track}
            />
          )
        })
        .with({ type: 'title' }, (item) => {
          return <Title {...item} flowValues={props.flowValues} />
        })
        .with({ type: 'yesNo' }, (item) => {
          return (
            <Choice
              {...item}
              options={[
                {
                  label: {
                    type: 'translatableText',
                    translationKey: 'sharedFlows_common:generic.inputs.yes',
                  },
                  value: true,
                },
                {
                  label: {
                    type: 'translatableText',
                    translationKey: 'sharedFlows_common:generic.inputs.no',
                  },
                  value: false,
                },
              ]}
              flowValues={props.flowValues}
            />
          )
        })
        .with({ type: 'columns', stackOnMobile: true }, (item) => (
          <Columns className="w-full flex-col gap-x-3 gap-y-10 md:flex-row [&>*]:flex-1">
            {(item.content ?? []).map((c, index) => (
              <StepContentRenderer
                key={index}
                {...props}
                hasMinHeight={false}
                hasVerticalPadding={false}
                elements={[c]}
              />
            ))}
          </Columns>
        ))
        .with({ type: 'columns' }, (item) => {
          const content = (item.content ?? []).map((c, index) => (
            <StepContentRenderer key={index} {...props} elements={[c]} />
          ))
          return (
            <Columns className="grid w-full grid-flow-col" space="medium" collapsable>
              {content}
            </Columns>
          )
        })
        .with({ type: 'calendly' }, (item) => {
          return (
            <Calendly
              {...item}
              flowValues={props.flowValues}
              track={props.track}
              flowName={props.flowName}
              onSubmit={props.onSubmit}
            />
          )
        })
        .with({ type: 'animation' }, (item) => {
          return <Animation {...item} flowValues={props.flowValues} />
        })
        .with({ type: 'otpInput' }, (item) => {
          if (!props.sendOtp) {
            throw new Error('Assertion: OTP config is required when using the OTP input')
          }
          return <OtpLogin {...item} config={props.sendOtp} flowValues={props.flowValues} />
        })
        .with({ type: 'addressAutocomplete' }, (item) => {
          return (
            <AddressAutocomplete
              {...item}
              flowValues={props.flowValues}
              market={props.market ?? SupportedMarketKey.De}
              language={props.language ?? Language.En}
              flowName={props.flowName}
            />
          )
        })
        .with({ type: 'richContent' }, (item) => {
          if (!props.richContentRenderer) {
            throw new Error(
              'Assertion: richContentRenderer is required when using the richContent element'
            )
          }
          return <RichContent richContentRenderer={props.richContentRenderer} {...item} />
        })
        .with({ type: 'stepper' }, (item) => {
          return <Stepper {...item} flowValues={props.flowValues} />
        })
        .with({ type: 'multiSelect' }, (item) => {
          return <MultiSelect {...item} flowValues={props.flowValues} />
        })
        .with({ type: 'listedItems' }, (item) => {
          return <ListedItems {...item} flowValues={props.flowValues} />
        })
        .with({ type: 'itemActionList' }, (item) => {
          if (!props.onSubmit) {
            throw new Error('Assertion: onSubmit is required when using the itemActionList element')
          }
          return (
            <ItemActionList
              {...item}
              flowValues={props.flowValues}
              submitHandler={props.onSubmit}
              isSubmitting={props.isSubmitting}
            />
          )
        })
        .with({ type: 'customComponent' }, (item) => {
          const CustomComponent = props.componentMap?.[item.name]
          if (!CustomComponent) {
            console.error(`Custom component not found: ${item.name}`)
            return <Text>{item.name}</Text>
          }
          return (
            <CustomComponent
              {...item.props}
              submitHandler={props.onSubmit}
              flowName={props.flowName}
            />
          )
        })
        .otherwise(() => {
          console.error(`Unknown element type: ${data.item.type}`)
          return <Text>{data.item.type}</Text>
        })

      if (FOCUSABLE_ELEMENT_TYPES.has(data.item.type)) {
        return <View className="safari-scale">{rendered}</View>
      }
      return rendered
    },
    [props]
  )

  if (props.elements.length === 0) {
    return null
  }

  // Makes components like the spinner look nicer if they are the only element
  if (props.elements.length === 1) {
    return (
      <FlowsServicesResponsesProvider servicesResponses={props.servicesResponses}>
        <ApiServiceProvider service={props.apiService} apiBases={props.apiBases}>
          <>
            {renderItem({
              index: 0,
              item: props.elements[0],
              separators: {
                highlight: () => undefined,
                unhighlight: () => undefined,
                updateProps: () => undefined,
              },
            })}
          </>
        </ApiServiceProvider>
      </FlowsServicesResponsesProvider>
    )
  }

  const visibleElements = props.elements.filter((element) => {
    if (!('condition' in element) || !element.condition) {
      return true
    }
    return evaluateCondition(element.condition, props.flowValues)
  })

  if (Platform.OS === 'web') {
    const getSpaceBefore = (item: AnyStepContentElement) => {
      const desktopSpace =
        isDesktop && 'spaceBeforeDesktop' in item ? item.spaceBeforeDesktop : undefined
      const space = 'spaceBefore' in item ? item.spaceBefore : undefined
      return SPACE[desktopSpace ?? space ?? props.space ?? 'medium']
    }
    return (
      <FlowsServicesResponsesProvider servicesResponses={props.servicesResponses}>
        <ApiServiceProvider service={props.apiService} apiBases={props.apiBases}>
          <View
            className={webStyles({
              hasMinHeight: props.hasMinHeight !== false,
              hasVerticalPadding: props.hasVerticalPadding !== false,
            })}
          >
            {props.progressive ? (
              <ProgressiveQuestions
                elements={visibleElements}
                getSpaceBefore={getSpaceBefore}
                getItemKey={getElementKey}
                renderItem={(item, index) => renderItem({ index, item, separators: NO_SEPARATORS })}
              />
            ) : (
              visibleElements.map((item, index) => (
                <React.Fragment key={getElementKey(item, index)}>
                  {index > 0 && <View style={{ height: getSpaceBefore(item) }} />}
                  {renderItem({ index, item, separators: NO_SEPARATORS })}
                </React.Fragment>
              ))
            )}
          </View>
        </ApiServiceProvider>
      </FlowsServicesResponsesProvider>
    )
  }

  return (
    <FlowsServicesResponsesProvider servicesResponses={props.servicesResponses}>
      <ApiServiceProvider service={props.apiService} apiBases={props.apiBases}>
        <View>
          <FlatList
            renderItem={renderItem}
            keyExtractor={(item, index) => `${JSON.stringify(item)}-${index}`}
            data={visibleElements}
            ItemSeparatorComponent={() => (
              <View style={{ height: SPACE[props.space ?? 'medium'] }} />
            )}
            contentContainerStyle={{
              paddingVertical: props.hasVerticalPadding === false ? 0 : SPACE.xsmall,
            }}
          />
        </View>
      </ApiServiceProvider>
    </FlowsServicesResponsesProvider>
  )
}
